import { tt } from '@agos/shared'

/**
 * The two switches that close the shop, from `GET /settings/general`.
 *
 * **Measured: both open** — `maintenanceMode: false`, `onlineOrdersEnabled: true`. So neither screen
 * draws today, which is exactly why their absence went unnoticed: the app took orders and was supposed
 * to. A franchise flipping either one would have found it still taking them.
 *
 * They are different states and legacy shows different screens, which is right:
 *
 * - **Maintenance** is *the site is being worked on* — nothing here is trustworthy, so nothing is shown.
 * - **Online orders off** is *we are open and not taking orders through this*. The menu could still be
 *   browsed in principle, but legacy blocks the page, and the reason holds: a menu with no way to order
 *   from it invites a customer to fill a basket they cannot submit.
 *
 * **Both fail open.** A settings call that did not answer must not close a restaurant that is trading —
 * the opposite of the payment settings, which fail closed because the cost of guessing wrong runs the
 * other way.
 */

export type ServiceState = 'open' | 'maintenance' | 'orders-off'

export function serviceStateOf(payload: unknown): ServiceState {
  if (typeof payload !== 'object' || payload === null) return 'open'
  const row = payload as { maintenanceMode?: unknown; onlineOrdersEnabled?: unknown }
  if (row.maintenanceMode === true) return 'maintenance'
  // `=== false` and not `!== true`: an absent field is a franchise that has never seen the setting, and
  // reading that as *closed* would shut every restaurant whose settings row predates the feature.
  if (row.onlineOrdersEnabled === false) return 'orders-off'
  return 'open'
}

export function serviceHeading(state: ServiceState): string {
  return state === 'maintenance' ? tt('Servisný režim') : tt('Online objednávky sú vypnuté')
}

export function serviceExplanation(state: ServiceState): string {
  return state === 'maintenance'
    ? tt('Reštaurácia práve vykonáva údržbu. Skúste to prosím neskôr, ďakujeme za trpezlivosť.')
    : tt('Momentálne neprijímame online objednávky. Kontaktujte nás telefonicky alebo sa nám ozvite neskôr.')
}
